import React from 'react';
import { ToolComponentProps } from './types';
import { Dices } from 'lucide-react';

interface RollDiceInput {
  sides?: number;
  count?: number;
}

export const RollDiceTool: React.FC<ToolComponentProps> = ({ 
  toolName, 
  input, 
  output, 
  isError,
  isExpanded,
  onToggle
}) => {
  const typedInput = input as RollDiceInput;
  const sides = typedInput?.sides || 6;
  const count = typedInput?.count || 1;
  
  // Parse the output to extract results
  const parseResults = (output: string): { rolls: number[], total: number } | null => {
    if (!output || typeof output !== 'string') return null;
    
    // Match patterns like "Rolled a d6: 4" or "Rolled 2 d6 dice: [3, 5] (total: 8)"
    const singleMatch = output.match(/Rolled a d\d+: (\d+)/);
    if (singleMatch) {
      return { rolls: [parseInt(singleMatch[1])], total: parseInt(singleMatch[1]) };
    }
    
    const multiMatch = output.match(/Rolled \d+ d\d+ dice: \[([\d, ]+)\] \(total: (\d+)\)/);
    if (multiMatch) {
      const rolls = multiMatch[1].split(',').map(n => parseInt(n.trim()));
      const total = parseInt(multiMatch[2]);
      return { rolls, total };
    }
    
    return null;
  };
  
  const results = output && !isError ? parseResults(output) : null;
  
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Dices className="h-4 w-4 text-primary" />
        <span className="font-semibold">Roll Dice</span>
        <span className="text-muted-foreground">
          ({count}d{sides})
        </span>
      </div>

      {results && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {results.rolls.map((roll, i) => (
              <div
                key={i}
                className="w-12 h-12 bg-primary/10 border-2 border-primary/30 rounded-lg flex items-center justify-center"
              >
                <span className="font-bold text-primary">
                  {roll}
                </span>
              </div>
            ))}
          </div>

          {results.rolls.length > 1 && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 text-center">
              <div className="text-muted-foreground mb-1">Total</div>
              <div className="font-bold text-primary">
                {results.total}
              </div>
            </div>
          )}
        </div>
      )}

      {output && isError && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
          <div className="text-destructive">
            Error: {output}
          </div>
        </div>
      )}

      {!output && input && (
        <div className="text-muted-foreground">
          Rolling {count}d{sides}...
        </div>
      )}
    </div>
  );
};